// Build a GDAL CLI command that clips a sub-window of a remote COG and writes
// it back out as a local COG — with NO reprojection (source and output are both
// EPSG:4326, so we just clip in native coordinates).

/**
 * @param {object} args
 * @param {string} args.cogUrl  remote COG URL (EPSG:4326)
 * @param {string} args.sceneId scene id, used for the output filename
 * @param {number[]} args.extent [minLon, minLat, maxLon, maxLat] in EPSG:4326
 * @param {number} [args.resolution] native deg/pixel, for an output-size estimate
 * @returns {{ command: string, html: string, outName: string, dims: {w:number,h:number}|null }}
 */
export function buildGdalClip({ cogUrl, sceneId, extent, resolution }) {
  const [minLon, minLat, maxLon, maxLat] = extent;
  const outName = `${sceneId}_clip.tif`;
  const f = (n) => n.toFixed(8);

  // gdal_translate -projwin takes upper-left then lower-right: ulx uly lrx lry.
  const parts = [
    'gdal_translate',
    '--config GDAL_DISABLE_READDIR_ON_OPEN EMPTY_DIR',
    '--config CPL_VSIL_CURL_ALLOWED_EXTENSIONS .tif',
    `-projwin ${f(minLon)} ${f(maxLat)} ${f(maxLon)} ${f(minLat)}`,
    '-projwin_srs EPSG:4326',
    '-of COG',
    '-co COMPRESS=LZW',
    '-co NUM_THREADS=ALL_CPUS',
    '-co BIGTIFF=IF_SAFER',
    `/vsicurl/${cogUrl}`,
    outName,
  ];
  const command = parts.join(' \\\n  ');

  let dims = null;
  if (resolution && resolution > 0) {
    dims = {
      w: Math.max(1, Math.round((maxLon - minLon) / resolution)),
      h: Math.max(1, Math.round((maxLat - minLat) / resolution)),
    };
  }

  return { command, html: highlight(command), outName, dims };
}

// Lightweight syntax highlighting for the on-screen command block.
function highlight(cmd) {
  return escapeHtml(cmd)
    .replace(/\/vsicurl\/\S+/g, (m) => `<span class="url">${m}</span>`)
    .replace(/(^|\s)(--?[A-Za-z_]+)/g, (_, s, flag) => `${s}<span class="flag">${flag}</span>`)
    .replace(
      /(-projwin(?:_srs)?|COMPRESS|NUM_THREADS|BIGTIFF|-of)\s+(\S+(?:\s+-?\d[\d.\s-]*\S)?)/g,
      (_, k, v) => `${k} <span class="val">${v}</span>`
    );
}

function escapeHtml(s) {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
}
